import type {
  SelectedLocation,
  GeoTargetingPayload,
  GeoTargetingValidation,
  SpecialAdCategory,
} from "@/types/geoTargeting";

/**
 * Formats selected locations into the Meta Ad Set API geo_locations payload
 */
export function formatGeoTargeting(
  locations: SelectedLocation[]
): GeoTargetingPayload {
  const countries: string[] = [];
  const regions: Array<{ key: string }> = [];
  const cities: Array<{
    key: string;
    radius?: number;
    distance_unit?: "mile" | "kilometer";
  }> = [];
  const zips: Array<{ key: string }> = [];

  for (const location of locations) {
    switch (location.type) {
      case "country":
        countries.push(location.key);
        break;
      case "region":
        regions.push({ key: location.key });
        break;
      case "city":
        cities.push({
          key: location.key,
          radius: location.radius,
          distance_unit: location.distance_unit,
        });
        break;
      case "zip":
        zips.push({ key: location.key });
        break;
    }
  }

  const geo_locations: GeoTargetingPayload["geo_locations"] = {};

  if (countries.length > 0) {
    geo_locations.countries = countries;
  }
  if (regions.length > 0) {
    geo_locations.regions = regions;
  }
  if (cities.length > 0) {
    geo_locations.cities = cities;
  }
  if (zips.length > 0) {
    geo_locations.zips = zips;
  }

  return { geo_locations };
}

/**
 * Validates geo targeting configuration
 */
export function validateGeoTargeting(
  locations: SelectedLocation[],
  specialAdCategory: SpecialAdCategory,
  allowedCountries?: string[]
): GeoTargetingValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if any locations selected
  if (locations.length === 0) {
    errors.push("At least one location must be selected");
    return { isValid: false, errors, warnings };
  }

  const isSpecialAdCategory = Boolean(specialAdCategory);

  // Validate each location
  for (const location of locations) {
    // Check country restrictions for Special Ad Categories
    if (
      isSpecialAdCategory &&
      allowedCountries &&
      allowedCountries.length > 0 &&
      location.country_code &&
      !allowedCountries.includes(location.country_code)
    ) {
      errors.push(
        `${location.name} is not in an allowed country for ${specialAdCategory} ads`
      );
    }

    // Validate city radius
    if (location.type === "city" && location.radius !== undefined) {
      const minRadius = isSpecialAdCategory ? 15 : 0.63;
      const maxRadius = location.distance_unit === "kilometer" ? 80 : 50;

      if (location.radius < minRadius) {
        if (isSpecialAdCategory) {
          errors.push(
            `${location.name} radius must be at least 15 ${
              location.distance_unit === "kilometer" ? "km" : "mi"
            } for Special Ad Categories`
          );
        } else {
          errors.push(
            `${location.name} radius must be at least ${minRadius} ${
              location.distance_unit === "kilometer" ? "km" : "mi"
            }`
          );
        }
      }

      if (location.radius > maxRadius) {
        errors.push(
          `${location.name} radius cannot exceed ${maxRadius} ${
            location.distance_unit === "kilometer" ? "km" : "mi"
          }`
        );
      }
    }
  }

  // Warnings
  if (locations.length > 50) {
    warnings.push(
      `You have ${locations.length} locations selected. Consider consolidating for better performance.`
    );
  }

  const hasCountries = locations.some((l) => l.type === "country");
  const hasCities = locations.some((l) => l.type === "city");
  if (hasCountries && hasCities) {
    warnings.push(
      "Mixing country-level and city-level targeting may result in overlapping audiences"
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}
